import { useState, useCallback, useRef, SetStateAction, Dispatch } from "react";

import {
    useRecommendations,
    AppRecRecommendationResult
} from "@atlassian/app-recommendations-kit";

import {
    CCPKey,
    CCPKeyToProductIdType,
    NUM_DISPLAY_RECOMMENDATIONS,
    ccpKeyToProductId
} from "../common/constants";

import { useAnalytics } from "./use-analytics";

const WAC_GLOBAL_NAV_PRODUCT_RECOMMENDATION_USE_CASE =
    "WACGlobalNavProductRecommendation";

export function useFetchRecommendations({ isMobile }: { isMobile?: boolean }) {
    const [isRecommendationLoading, setIsRecommendationLoading] =
        useState(false);
    const isRecommendationLoadedRef = useRef(false);
    const [recommendations, setRecommendations] = useState<CCPKey[]>([]);
    const {
        fireFetchGrowthRecommendationSucceeded,
        fireFetchGrowthRecommendationFailed
    } = useAnalytics({ isMobile });

    const { getRecommendations } = useRecommendations({
        contextResolver: {
            product: "marketing",
            subproduct: "productsDropdown",
            useCase: WAC_GLOBAL_NAV_PRODUCT_RECOMMENDATION_USE_CASE
        }
    });

    const getProductRecommendations = useCallback(
        async ({
            setIsGlobalNavProductRecommendationsExperimentFailed
        }: {
            setIsGlobalNavProductRecommendationsExperimentFailed: Dispatch<
                SetStateAction<boolean>
            >;
        }) => {
            if (isRecommendationLoading || isRecommendationLoadedRef.current) {
                return;
            }

            const startTime = performance.now();

            try {
                setIsRecommendationLoading(true);

                const result = await getRecommendations({
                    first: NUM_DISPLAY_RECOMMENDATIONS,
                    rerank: asEntityIdList(ccpKeyToProductId),
                    config: {
                        cacheConfig: {
                            ttl: 60,
                            /**
                             * The use case won't be required in the next patch release
                             */
                            useCase:
                                WAC_GLOBAL_NAV_PRODUCT_RECOMMENDATION_USE_CASE
                        }
                    }
                });

                const { response: { data } = {} } = result;

                if (isValidResponse(data)) {
                    const recommendedProductIds = data.map(
                        ({ entityId }) => entityId as CCPKey
                    );

                    setRecommendations(recommendedProductIds);

                    const elapsedTime = performance.now() - startTime;
                    fireFetchGrowthRecommendationSucceeded(
                        recommendedProductIds,
                        elapsedTime
                    );

                    setIsRecommendationLoading(false);
                    isRecommendationLoadedRef.current = true;
                } else {
                    throw new Error(
                        "Invalid response from App Recommendations API"
                    );
                }
            } catch (error: unknown) {
                const elapsedTime = performance.now() - startTime;
                fireFetchGrowthRecommendationFailed(error, elapsedTime);

                setIsGlobalNavProductRecommendationsExperimentFailed(true);
                setIsRecommendationLoading(false);
                isRecommendationLoadedRef.current = true;
            }
        },
        [
            fireFetchGrowthRecommendationFailed,
            fireFetchGrowthRecommendationSucceeded,
            getRecommendations,
            isRecommendationLoading
        ]
    );

    return {
        getProductRecommendations,
        isRecommendationLoading,
        recommendations
    };
}

const asEntityIdList = (entities: CCPKeyToProductIdType) =>
    Object.keys(entities).map((key) => ({ entityId: key }));

/**
 * Ensure the response is valid type
 */
const isValidResponse = (
    data: AppRecRecommendationResult | undefined
): data is AppRecRecommendationResult => {
    return Array.isArray(data);
};
